import React from 'react';
import { Table, Input, Button, Radio } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { F1042, F103 } from '../../../services/companyApi';

interface Props {
  isEditing: boolean;
  contacts: F1042[];
  setContacts: (v: F1042[]) => void;
  companyCode: string;
  businessCards: F103[];
  setBusinessCards: (v: F103[]) => void;
}

const Tab3Contacts: React.FC<Props> = React.memo(({ isEditing, contacts, setContacts, companyCode, businessCards, setBusinessCards }) => {
  const updateRow = (idx: number, field: keyof F1042, value: string) => {
    const next = [...contacts];
    next[idx] = { ...next[idx], [field]: value };
    setContacts(next);
  };

  const addRow = () => {
    setContacts([...contacts, {
      f1042_01: companyCode, f1042_02: '', f1042_11: '', f1042_12: '', f1042_13: '', f1042_14: '', f1042_15: '',
    }]);
  };

  const deleteRow = (idx: number) => {
    setContacts(contacts.filter((_, i) => i !== idx));
  };

  const columns = [
    { title: '職務', dataIndex: 'f1042_11', width: 90,
      render: (v: string, _: F1042, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateRow(i, 'f1042_11', e.target.value)} />
        : v },
    { title: '姓名', dataIndex: 'f1042_02', width: 100,
      render: (v: string, _: F1042, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateRow(i, 'f1042_02', e.target.value)} />
        : v },
    { title: '職稱', dataIndex: 'f1042_12', width: 100,
      render: (v: string, _: F1042, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateRow(i, 'f1042_12', e.target.value)} />
        : v },
    { title: '電話', dataIndex: 'f1042_13', width: 130,
      render: (v: string, _: F1042, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateRow(i, 'f1042_13', e.target.value)} />
        : v },
    { title: '分機', dataIndex: 'f1042_14', width: 80,
      render: (v: string, _: F1042, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateRow(i, 'f1042_14', e.target.value)} />
        : v },
    { title: 'Email', dataIndex: 'f1042_15', ellipsis: true,
      render: (v: string, _: F1042, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateRow(i, 'f1042_15', e.target.value)} />
        : v },
    ...(isEditing ? [{
      title: '', width: 40,
      render: (_: unknown, __: F1042, i: number) => (
        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => deleteRow(i)} />
      ),
    }] : []),
  ];

  const updateCard = (idx: number, field: keyof F103, value: string) => {
    const next = [...businessCards];
    next[idx] = { ...next[idx], [field]: value };
    setBusinessCards(next);
  };

  const addCard = () => {
    setBusinessCards([...businessCards, {
      f103_01: companyCode, f103_12: '', f103_14: '', f103_21: '', f103_22: '',
    }]);
  };

  const deleteCard = (idx: number) => {
    setBusinessCards(businessCards.filter((_, i) => i !== idx));
  };

  const cardColumns = [
    { title: '姓名', dataIndex: 'f103_01', width: 100,
      render: (v: string) => v || companyCode },
    { title: '英文姓名', dataIndex: 'f103_12', width: 130,
      render: (v: string, _: F103, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateCard(i, 'f103_12', e.target.value)} maxLength={30} />
        : v },
    { title: '性別', dataIndex: 'f103_14', width: 120,
      render: (v: string, _: F103, i: number) => isEditing
        ? <Radio.Group size="small" value={v} onChange={e => updateCard(i, 'f103_14', e.target.value)}>
            <Radio value="M">男</Radio><Radio value="F">女</Radio>
          </Radio.Group>
        : v === 'M' ? '男' : v === 'F' ? '女' : v },
    { title: '聯絡電話', dataIndex: 'f103_21', width: 140,
      render: (v: string, _: F103, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateCard(i, 'f103_21', e.target.value)} maxLength={20} />
        : v },
    { title: '行動電話', dataIndex: 'f103_22', width: 140,
      render: (v: string, _: F103, i: number) => isEditing
        ? <Input size="small" value={v} onChange={e => updateCard(i, 'f103_22', e.target.value)} maxLength={16} />
        : v },
    ...(isEditing ? [{
      title: '', width: 40,
      render: (_: unknown, __: F103, i: number) => (
        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => deleteCard(i)} />
      ),
    }] : []),
  ];

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">聯絡人</h4>
        {isEditing && (
          <Button size="small" icon={<PlusOutlined />} onClick={addRow} className="mb-2">新增聯絡人</Button>
        )}
        <Table dataSource={contacts} columns={columns} rowKey={(_, i) => String(i)}
          size="small" pagination={false} scroll={{ y: 200 }} />
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">名片</h4>
        {isEditing && (
          <Button size="small" icon={<PlusOutlined />} onClick={addCard} className="mb-2">新增名片</Button>
        )}
        <Table dataSource={businessCards} columns={cardColumns} rowKey={(_, i) => String(i)}
          size="small" pagination={false} scroll={{ y: 160 }} />
      </div>
    </div>
  );
});

export default Tab3Contacts;
